import React from 'react';
import Bugsnag, { Client, OnErrorCallback } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { FRONTEND_VERSION, isDev, BUGSNAG_API_KEY } from 'utils/env';
import { store } from '../store';
import { addAlert } from 'modules/alert';
import { errorBoundaryMessage } from 'constants/errorMessages';

class ErrorLogger {
  private bugsnagClient: Client | null = null;

  public init() {
    if (!BUGSNAG_API_KEY || isDev) {
      return;
    }

    this.bugsnagClient = Bugsnag.start({
      apiKey: BUGSNAG_API_KEY,
      plugins: [new BugsnagPluginReact()],
      appVersion: FRONTEND_VERSION
    });
  }

  public getErrorBoundary() {
    return ({ children }: { children: JSX.Element }) => {
      if (this.bugsnagClient) {
        const ErrorBoundary = this.bugsnagClient.getPlugin('react')?.createErrorBoundary(React);
        const onError: OnErrorCallback = event => {
          const { dispatch } = store;
          // eslint-disable-next-line
          console.error('Uncaught error:', event.originalError);
          dispatch(addAlert(errorBoundaryMessage.title, errorBoundaryMessage.message, true));
        };
        const FallbackComponent = () => <div style={{ height: '100%', width: '100%' }} />;
        return (
          <ErrorBoundary onError={onError} FallbackComponent={FallbackComponent}>
            {children}
          </ErrorBoundary>
        );
      }

      return children;
    };
  }

  public trigger(
    error: string | Error,
    status: 'error' | 'debug' | 'info' | 'warn' | 'trace' | 'log' = 'log'
  ) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console[status](error);
    }

    if (this.bugsnagClient && ['error', 'warn'].includes(status)) {
      this.bugsnagClient.notify(error);
    }
  }
}

export default new ErrorLogger();
